if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Listen for cart updates to update the plant-pads sidebar
        document.addEventListener('cart:updated', (event) => {
          if (event.detail && event.detail.cartData) {
            this.updatePlantPadsSidebar(event.detail.cartData);
          }
        });

        // Initialize sidebar with current cart contents if we're on a plant-pods page
        if (this.closest('[id^="collection-"]')) {
          this.initializePlantPadsSidebar();
          this.initializeMobileSidebar();
          // Initialize mobile count with 0 (9 needed)
          this.updateMobilePlantPadsCount(0);
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Console.log the metafield value
        const addBehaviour = this.submitButton.dataset.addBehaviour;
        console.log('Add to cart clicked - metafield value:', addBehaviour);

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // Check if this product is in the plant-pods-collection section
            const isPlantPodsSection = this.closest('[id^="collection-"]') !== null;
            
            // Handle different add behaviours
            const addBehaviour = this.submitButton.dataset.addBehaviour;
            
            if (addBehaviour === 'pads_and_accessories' || addBehaviour === 'pads_and_scissors') {
              // For pads_and_accessories or pads_and_scissors: Add to cart but don't show cart, redirect to select-plant
              const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                }).then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                });
              this.error = false;
              
              // Don't render cart contents, redirect instead
              window.location.href = '/pages/select-plant-pads?add_behaviour=' + encodeURIComponent(addBehaviour);
              return;
            } else if (isPlantPodsSection) {
              // For plant-pods section: Add to cart silently and update sidebar
              const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
              if (!this.error) {
                // Update cart count without triggering notifications
                if (this.cart) {
                  this.cart.renderContents(response);
                }
              }
              this.error = false;
              
              // Update the plant-pads sidebar with cart contents
              this.updatePlantPadsSidebar(response);
              
              // Prevent cart notification from showing
              const cartNotification = document.querySelector('cart-notification');
              if (cartNotification) {
                cartNotification.hide();
              }
              
              // Also prevent cart drawer from showing
              const cartDrawer = document.querySelector('cart-drawer');
              if (cartDrawer) {
                cartDrawer.hide();
              }
              
              // Prevent any cart notifications from showing
              document.dispatchEvent(new CustomEvent('cart:updated', {
                detail: { 
                  cartData: response,
                  preventNotification: true 
                }
              }));
              return;
            } else {
              // Default behaviour: Normal cart update and display
              const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                }).then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                });
              this.error = false;
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      CartPerformance.measure("add:paint-updated-sections", () => {
                        this.cart.renderContents(response);
                      });
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                });
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      updatePlantPadsSidebar(cartData) {
        const sidebar = document.querySelector('.plant-pads-sidebar-content');
        if (!sidebar) return;

        console.log('Cart data received:', cartData);

        // Check if this is a single item response or full cart response
        let items = [];
        if (cartData.items) {
          // Full cart response
          items = cartData.items;
        } else if (cartData.product_title) {
          // Single item response - fetch full cart
          this.fetchAndUpdateSidebar();
          return;
        }

        // Check if Launch Pack is in cart
        const hasLaunchPack = items.some(item => item.product_title && item.product_title.includes('Launch Pack'));
        
        // Filter cart items that have the "PlantPad" tag
        const plantPadItems = items.filter(item => {
          console.log('Checking item:', item.product_title, 'Tags:', item.product_tags);
          // Check if product has PlantPad tag - we'll need to fetch product data
          return item.product_title && this.isPlantPadProduct(item.product_title);
        });

        console.log('PlantPad items found:', plantPadItems);

        if (plantPadItems.length === 0 && !hasLaunchPack) {
          const buttonLink = this.getContinueCheckoutLink();
          sidebar.innerHTML = `
            <h3>Plant Pads Selection</h3>
            <div class="sidebar-total">
              <div class="sidebar-total-header h2">
                <span class="sidebar-total-green">9</span> ${this.getSidebarTotalHeader()}
              </div>
              <div class="sidebar-total-info">
                ${this.getSidebarTotalInfo()}
              </div>
            </div>
            <a href="${buttonLink}" class="button">Continue Checkout Anyway</a>
          `;
          return;
        }

        let sidebarContent = '<h3>Your Plant Pads</h3>';
        
        // If Launch Pack is present, show it first
        if (hasLaunchPack) {
          const launchPackItem = items.find(item => item.product_title && item.product_title.includes('Launch Pack'));
          const launchPackKey = launchPackItem ? launchPackItem.key : '';
          sidebarContent += `<p class="sidebar-line-item" data-item-key="${launchPackKey}"><span class="sidebar-qty">1 x</span> <span class="sidebar-product">Launch Box Set 12-Pack <span class="sidebar-selling-plan">(One-time purchase)</span></span><button class="sidebar-remove-btn" data-item-key="${launchPackKey}" aria-label="Remove item">×</button></p>`;
        }
        
        // Show other plant pad items
        plantPadItems.forEach(item => {
          const subscriptionText = item.selling_plan_allocation ? 
            `(${item.selling_plan_allocation.selling_plan.name})` : 
            '(One-time purchase)';
          
          sidebarContent += `<p class="sidebar-line-item" data-item-key="${item.key}"><span class="sidebar-qty">${item.quantity} x</span> <span class="sidebar-product">${item.product_title} <span class="sidebar-selling-plan">${subscriptionText}</span></span><button class="sidebar-remove-btn" data-item-key="${item.key}" aria-label="Remove item">×</button></p>`;
        });

        // Calculate total plant pads
        let totalPlantPads = 0;
        
        // Add Launch Pack contribution (36 plant pads)
        if (hasLaunchPack) {
          totalPlantPads += 36;
        }
        
        // Add other plant pad items
        plantPadItems.forEach(item => {
          const plantPadsPerItem = this.getPlantPadsPerProduct(item.product_title);
          totalPlantPads += (item.quantity * plantPadsPerItem);
        });
        
        const maxCapacity = 9;
        const remainingPads = Math.max(0, maxCapacity - totalPlantPads);
        
        // Determine header content and class based on whether cart is full
        const isFull = totalPlantPads >= maxCapacity;
        const headerClass = isFull ? 'sidebar-total-header sidebar-total-header-full' : 'sidebar-total-header';
        const headerContent = isFull ? this.getSidebarTotalHeaderFull() : this.getSidebarTotalHeader(remainingPads);
        
        // Determine button text and link based on whether cart is full and URL parameter
        const buttonText = isFull ? 'Continue Checkout' : 'Continue Checkout Anyway';
        const buttonLink = this.getContinueCheckoutLink();
        
        sidebarContent += `
          <div class="sidebar-total">
            <div class="${headerClass} h2">
              ${isFull ? '' : '<span class="sidebar-total-green">' + remainingPads + '</span> '}${headerContent}
            </div>
            <div class="sidebar-total-info">
              ${this.getSidebarTotalInfo()}
            </div>
          </div>
          <a href="${buttonLink}" class="button">${buttonText}</a>
        `;

        sidebar.innerHTML = sidebarContent;
        
        // Ensure sidebar maintains correct position after dynamic update
        const sidebarElement = document.querySelector('.plant-pads-sidebar');
        if (sidebarElement) {
          const isMobile = window.innerWidth <= 989;
          
          if (isMobile) {
            // Move to body if not already there (to avoid stacking context issues)
            if (sidebarElement.parentElement !== document.body) {
              document.body.appendChild(sidebarElement);
            }
            sidebarElement.style.zIndex = '2147483647';
            sidebarElement.style.position = 'fixed';
          } else {
            // Desktop: ensure styles are reset
            sidebarElement.style.zIndex = '';
            sidebarElement.style.position = '';
          }
        }
        
        // Update mobile count
        this.updateMobilePlantPadsCount(totalPlantPads);
        
        // Add event listeners to remove buttons
        const removeButtons = sidebar.querySelectorAll('.sidebar-remove-btn');
        removeButtons.forEach(button => {
          button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const itemKey = button.getAttribute('data-item-key');
            if (itemKey) {
              this.removeCartItem(itemKey);
            }
          });
        });
      }

      async removeCartItem(itemKey) {
        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: itemKey,
              quantity: 0
            })
          });

          if (!response.ok) {
            throw new Error('Failed to remove item from cart');
          }

          const cartData = await response.json();
          
          // Update the sidebar with new cart data
          this.updatePlantPadsSidebar(cartData);
          
          // Dispatch cart update event for other components
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cartData }
          }));
        } catch (error) {
          console.error('Error removing item from cart:', error);
        }
      }

      async initializePlantPadsSidebar() {
        try {
          const response = await fetch('/cart.js');
          const cartData = await response.json();
          this.updatePlantPadsSidebar(cartData);
        } catch (error) {
          console.error('Error fetching cart data:', error);
        }
      }

      async fetchAndUpdateSidebar() {
        try {
          const response = await fetch('/cart.js');
          const cartData = await response.json();
          this.updatePlantPadsSidebar(cartData);
        } catch (error) {
          console.error('Error fetching cart data:', error);
        }
      }

      isPlantPadProduct(productTitle) {
        return productTitle && (
          productTitle.includes('3-Pack') ||
          productTitle.includes('12-Pack')
        );
      }

      getPlantPadsPerProduct(productTitle) {
        if (!productTitle) return 1;
        
        // Check for 12-Pack products (36 plant pads - fills the farm)
        if (productTitle.includes('12-Pack')) {
          return 36;
        }
        
        // Check for 3-Pack products
        if (productTitle.includes('3-Pack')) {
          return 3;
        }
        
        // Default to 1 plant pad per product
        return 1;
      }

      getSidebarTotalHeader() {
        // Get the header text from the section settings
        const section = this.closest('[id^="collection-"]');
        if (section) {
          const headerElement = section.querySelector('[data-sidebar-total-header]');
          return headerElement ? headerElement.textContent : 'Plant Pads left to fill your Roto Farm.';
        }
        return 'Plant Pads left to fill your Roto Farm.';
      }

      getSidebarTotalInfo() {
        // Get the info text from the section settings
        const section = this.closest('[id^="collection-"]');
        if (section) {
          const infoElement = section.querySelector('[data-sidebar-total-info]');
          return infoElement ? infoElement.innerHTML : '<p>Roto Farm grows food fast, so Plant Pads last about 3 months. Add extra for a constant supply of fresh home-grown herbs and vegetables!</p>';
        }
        return '<p>Roto Farm grows food fast, so Plant Pads last about 3 months. Add extra for a constant supply of fresh home-grown herbs and vegetables!</p>';
      }

      getSidebarTotalHeaderFull() {
        // Get the full header text from the section settings
        const section = this.closest('[id^="collection-"]');
        if (section) {
          const headerElement = section.querySelector('[data-sidebar-total-header-full]');
          return headerElement ? headerElement.textContent : 'Your Roto Farm is full!';
        }
        return 'Your Roto Farm is full!';
      }

      getContinueCheckoutLink() {
        // Get the add_behaviour parameter from the URL
        const urlParams = new URLSearchParams(window.location.search);
        const addBehaviour = urlParams.get('add_behaviour');
        
        // Return different links based on the add_behaviour parameter
        if (addBehaviour === 'pads_and_accessories') {
          return '/pages/select-accessories?add_behaviour=checkout';
        } else if (addBehaviour === 'pads_and_scissors') {
          return '/pages/add-scissors?add_behaviour=checkout';
        } else {
          // Default fallback
          return '/cart';
        }
      }

      updateMobilePlantPadsCount(totalPlantPads) {
        const mobileCountElement = document.querySelector('.mobile-plant-pads-count');
        const mobileTextElement = document.querySelector('.mobile-plant-pads-text');
        
        console.log('Updating mobile count:', totalPlantPads);
        
        if (mobileCountElement && mobileTextElement) {
          const maxCapacity = 9;
          const remainingPads = Math.max(0, maxCapacity - totalPlantPads);
          
          console.log('Remaining pads:', remainingPads);
          
          if (totalPlantPads >= maxCapacity) {
            // Show "Your Roto Farm is full!" when full
            mobileCountElement.textContent = '';
            mobileTextElement.textContent = 'Your Roto Farm is full!';
            mobileTextElement.classList.add('sidebar-total-green');
          } else {
            // Show remaining pads needed with green number
            mobileCountElement.textContent = remainingPads;
            mobileTextElement.textContent = remainingPads === 1 ? ' pad left to fill Roto Farm' : ' pads left to fill Roto Farm';
            mobileCountElement.classList.add('sidebar-total-green');
            mobileTextElement.classList.remove('sidebar-total-green');
          }
          
          console.log('Updated mobile bar:', mobileCountElement.textContent + mobileTextElement.textContent);
        } else {
          console.log('Mobile elements not found');
        }
      }

      initializeMobileSidebar() {
        const sidebar = document.querySelector('.plant-pads-sidebar');
        const sidebarBar = sidebar ? sidebar.querySelector('.plant-pads-sidebar-bar') : null;
        
        if (sidebar && sidebarBar) {
          const isMobile = window.innerWidth <= 989;
          
          // Move sidebar to body level on mobile and ensure correct z-index
          if (isMobile) {
            // Move to body if not already there (to avoid stacking context issues)
            if (sidebar.parentElement !== document.body) {
              document.body.appendChild(sidebar);
            }
            sidebar.style.zIndex = '2147483647';
            sidebar.style.position = 'fixed';
            
            // Re-query after moving to ensure we have the right reference
            const newSidebar = document.querySelector('.plant-pads-sidebar');
            const newSidebarBar = newSidebar ? newSidebar.querySelector('.plant-pads-sidebar-bar') : null;
            
            if (newSidebarBar) {
              // Remove old listeners by cloning
              const newBarClone = newSidebarBar.cloneNode(true);
              newSidebarBar.parentNode.replaceChild(newBarClone, newSidebarBar);
              
              // Add click handler (only needed on mobile)
              newBarClone.addEventListener('click', function(e) {
                // Don't toggle if clicking on the checkout button
                if (e.target.closest('.plant-pads-checkout-btn')) {
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
                newSidebar.classList.toggle('is-open');
                console.log('Sidebar toggled from product-form, is-open:', newSidebar.classList.contains('is-open'));
              });
            }
          } else {
            // Desktop: ensure sidebar styles are reset
            sidebar.style.zIndex = '';
            sidebar.style.position = '';
          }
        }
      }


    }
  );
}
